import { Injectable, PLATFORM_ID, computed, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';

export interface KeycloakConfig {
  url: string;
  realm: string;
  clientId: string;
}

export type FiscalTransport = 'serial' | 'tcp';

export interface FiscalConfig {
  driverId: string;
  transport: FiscalTransport;
  port: string;
  baudRate: number;
  host: string;
  tcpPort: number;
}

export interface PosRuntimeConfig {
  apiUrl: string;
  keycloak: KeycloakConfig;
  fiscal: FiscalConfig;
}

const STORAGE_KEY = 'pos_runtime_config';

const DEFAULT_FISCAL: FiscalConfig = { driverId: 'mock', transport: 'serial', port: '', baudRate: 9600, host: '', tcpPort: 9100 };

// Stare instalacje miały sterownik 'serial' (szkielet szeregowy ~ Posnet). Bez migracji
// nieznane id kończyłoby się błędem unknown_fiscal_driver przy każdym wydruku.
function migrateFiscal(fiscal: FiscalConfig): FiscalConfig {
  if (fiscal.driverId === 'serial') {
    return { ...fiscal, driverId: 'posnet', transport: 'serial' };
  }
  return fiscal;
}

@Injectable({ providedIn: 'root' })
export class ConfigService {
  private readonly platformId = inject(PLATFORM_ID);

  private readonly _config = signal<PosRuntimeConfig>(this.load());

  readonly config = this._config.asReadonly();
  readonly apiUrl = computed(() => this._config().apiUrl);
  readonly keycloak = computed(() => this._config().keycloak);
  readonly fiscal = computed(() => this._config().fiscal);

  update(patch: Partial<PosRuntimeConfig>): void {
    const next: PosRuntimeConfig = {
      ...this._config(),
      ...patch,
      keycloak: { ...this._config().keycloak, ...(patch.keycloak ?? {}) },
      fiscal: { ...this._config().fiscal, ...(patch.fiscal ?? {}) },
    };
    this._config.set(next);
    if (isPlatformBrowser(this.platformId)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
  }

  private load(): PosRuntimeConfig {
    const fallback: PosRuntimeConfig = {
      apiUrl: environment.apiUrl,
      keycloak: { ...environment.keycloak },
      fiscal: { ...DEFAULT_FISCAL },
    };
    try {
      if (typeof localStorage === 'undefined') return fallback;
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw) as Partial<PosRuntimeConfig>;
      return {
        apiUrl: parsed.apiUrl ?? fallback.apiUrl,
        keycloak: { ...fallback.keycloak, ...(parsed.keycloak ?? {}) },
        fiscal: migrateFiscal({ ...fallback.fiscal, ...(parsed.fiscal ?? {}) }),
      };
    } catch {
      return fallback;
    }
  }
}
